import { Iphone } from '@components/Iphone/Iphone'
import {
  DisplayModeOptions,
  LayoutDirectionOptions,
} from '@models/EventsCollection'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBRMFormActionProps } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { requiredRule } from '@utils/rules'
import type { SliderSingleProps } from 'antd'
import {
  Alert,
  Divider,
  Flex,
  Form,
  Segmented,
  Slider,
  Space,
  theme,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Localizable } from '../../localizable/Localizable'
import { EventsCollectionPreview } from '../PreviewCard'

const { Text } = Typography
const Create = ({ form, formProps }: SBRMFormActionProps) => {
  const { token } = theme.useToken()

  const [eventsCollectionType, setEventsCollectionType] = useState<
    'static' | 'dynamic'
  >('static')

  const marks: SliderSingleProps['marks'] = {
    0: 'Today',
    7: '+7',
    15: '+15',
    30: '+30',
    60: '+60',
    80: '+∞',
  }
  const formatter: NonNullable<SliderSingleProps['tooltip']>['formatter'] = (
    value
  ) => (value == 80 ? 'All' : `+ ${value} days`)

  const range = Form.useWatch('rangeQuery', form) || [7, 15]
  const displayIsVertical = Form.useWatch('direction', form)
  const displayMode = Form.useWatch('displayMode', form)
  const title = Form.useWatch(['fr', 'title'], form)

  return (
    <Form {...formProps} form={form} layout="vertical">
      <Segmented
        onChange={(value) => {
          form.resetFields(['events', 'rangeQuery']) // Reset fields when changing type
          setEventsCollectionType(value as 'static' | 'dynamic')
        }}
        options={[
          { value: 'static', label: 'Static' },
          { value: 'dynamic', label: 'Dynamic' },
        ]}
        block
      />
      {eventsCollectionType === 'static' && (
        <Form.Item
          style={{ marginTop: 20 }}
          name="events"
          rules={[requiredRule()]}
          label={'Select events'}
        >
          <SBAsyncSelect
            entity={Entity.event}
            multiple
            allowClear
            searchFieldName="title"
          />
        </Form.Item>
      )}

      {eventsCollectionType === 'dynamic' && (
        <Space
          orientation="vertical"
          className="w-full"
          style={{ marginTop: 20, marginBottom: 20 }}
        >
          <Form.Item name="rangeQuery" rules={[requiredRule()]} noStyle>
            <Slider
              style={{ marginRight: 20, marginLeft: 20 }}
              range
              marks={marks}
              max={80}
              tooltip={{ formatter }}
            />
          </Form.Item>

          <Alert
            title={
              <Space orientation="vertical">
                <Text>
                  This collection will dynamically display all events between{' '}
                  <b>today{range[0] !== 0 ? ` + ${range[0]} days` : ''}</b> and{' '}
                  <b>
                    {range[1] !== 80 ? `today + ${range[1]} days` : 'all time'}.
                  </b>
                  The selection will update automatically as time passes.{' '}
                </Text>
                <Text>
                  Example for today, this collection will display all events
                  between:{' '}
                  <b>{dayjs().add(range[0], 'days').format('ddd DD MMM')}</b>{' '}
                  and{' '}
                  <b>{dayjs().add(range[1], 'days').format('ddd DD MMM')}</b>
                </Text>
              </Space>
            }
          />
        </Space>
      )}
      <Divider style={{ marginTop: 0, marginBottom: 20 }} />
      <Localizable.Input
        name="title"
        rules={[requiredRule()]}
        label={'Title'}
        max={70}
      />
      <Form.Item
        rules={[requiredRule()]}
        label="Direction"
        name="direction"
        initialValue={true}
      >
        <Segmented options={LayoutDirectionOptions} block />
      </Form.Item>
      {displayIsVertical && (
        <Form.Item
          rules={[requiredRule()]}
          label="Cell display mode"
          name="displayMode"
          initialValue={'full'}
        >
          <Segmented options={DisplayModeOptions} block />
        </Form.Item>
      )}
      <Flex style={{ marginTop: 20 }}>
        <Iphone background={token.colorBgContainer}>
          <div style={{ paddingTop: 40 }}>
            <EventsCollectionPreview
              displayIsVertical={displayIsVertical}
              displayMode={displayMode}
              title={title}
            />
          </div>
        </Iphone>
      </Flex>
    </Form>
  )
}

export default Create
